import React, { useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { inventoryAPI } from '../../services';
import { toast } from 'react-toastify';
import QtyDual from '../../utils/inventoryUnit';

const TARGET_OPTIONS = [
  { value: 'par_level', label: 'Định mức' },
  { value: 'min_stock', label: 'Ngưỡng tối thiểu' },
  { value: 'both', label: 'Cả hai' },
];

const TARGET_LABEL = { par_level: 'Định mức', min_stock: 'Ngưỡng tối thiểu' };

function statusBadge(cell) {
  if (!cell) return null;
  if (cell.skip_reason === 'no_base') return <Badge bg="secondary">Chưa có mức chung</Badge>;
  if (cell.skip_reason === 'zero') return <Badge bg="secondary">Ra 0 — bỏ qua</Badge>;
  if (cell.will_write && cell.will_overwrite) return <Badge bg="warning" text="dark">Đè</Badge>;
  if (cell.will_write) return <Badge bg="success">Mới</Badge>;
  return <Badge bg="secondary">Giữ nguyên (đã có riêng)</Badge>;
}

function TargetCell({ cell, material }) {
  if (!cell) return <td className="text-end text-muted">—</td>;
  return (
    <td className="text-end">
      <div className="small text-muted">
        Chung: {cell.common != null ? <QtyDual quantityInBase={cell.common} material={material} /> : '—'}
      </div>
      <div className="small text-muted">
        Riêng hiện tại: {cell.current_override != null ? <QtyDual quantityInBase={cell.current_override} material={material} /> : '—'}
      </div>
      <div>
        Sẽ thành: {cell.new_value != null ? <QtyDual quantityInBase={cell.new_value} material={material} /> : '—'}
      </div>
      <div className="mt-1">{statusBadge(cell)}</div>
    </td>
  );
}

/**
 * #155519 — áp mức chung (Định mức / Ngưỡng tối thiểu) sang mức riêng cho HÀNG LOẠT NVL của 1 chi
 * nhánh theo tỷ lệ %. Chỉ Admin trung tâm dùng (BE chặn bằng RolesGuard, FE tự kiểm trước khi mở).
 * Bắt buộc XEM TRƯỚC mới được ÁP DỤNG — mọi thay đổi tham số sau khi xem trước sẽ xoá bản xem
 * trước cũ, tránh áp nhầm theo tham số đã đổi.
 */
export default function ApplyRatioModal({ show, onHide, branches, currentBranchId, materials, onSuccess }) {
  const [branchId, setBranchId] = useState(currentBranchId || '');
  const [target, setTarget] = useState('par_level');
  const [ratio, setRatio] = useState('');
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingApply, setLoadingApply] = useState(false);

  const materialById = useMemo(
    () => new Map((materials || []).map((m) => [m.id, m])),
    [materials],
  );

  const targetKeys = target === 'both' ? ['par_level', 'min_stock'] : [target];

  const reset = () => {
    setPreview(null);
    setRatio('');
    setOverwriteExisting(false);
    setTarget('par_level');
    setBranchId(currentBranchId || '');
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  const invalidateOnChange = (setter) => (value) => {
    setPreview(null);
    setter(value);
  };

  const currentBranchName = branches?.find((b) => b.id === branchId)?.name || '';

  const handlePreview = async () => {
    const ratioNum = Number(ratio);
    if (!ratio || ratioNum <= 0) {
      toast.error('Tỷ lệ phải lớn hơn 0');
      return;
    }
    if (!branchId) {
      toast.error('Vui lòng chọn chi nhánh');
      return;
    }
    try {
      setLoadingPreview(true);
      const result = await inventoryAPI.previewApplyRatio(branchId, {
        target,
        ratio: ratioNum,
        overwrite_existing: overwriteExisting,
      });
      setPreview(result);
    } catch (err) {
      toast.error(err?.message || 'Không thể xem trước');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleApply = async () => {
    if (!preview) return;
    if (preview.summary.applied === 0) {
      toast.error('Không có NVL nào để áp — kiểm tra lại tỷ lệ hoặc tuỳ chọn ghi đè');
      return;
    }
    if (overwriteExisting && preview.summary.overwritten > 0) {
      const ok = window.confirm(
        `Sẽ ĐÈ ${preview.summary.overwritten} mức riêng đã đặt tay trước đó tại chi nhánh "${currentBranchName}". Tiếp tục?`,
      );
      if (!ok) return;
    }
    try {
      setLoadingApply(true);
      const result = await inventoryAPI.applyRatio(branchId, {
        target,
        ratio: Number(ratio),
        overwrite_existing: overwriteExisting,
      });
      toast.success(
        `Đã đặt ${result.summary.applied} NVL, bỏ qua ${result.summary.skipped_no_base} NVL (chưa có mức chung), đè ${result.summary.overwritten} NVL`,
      );
      if (onSuccess) onSuccess();
      handleClose();
    } catch (err) {
      toast.error(err?.message || 'Không thể áp dụng');
    } finally {
      setLoadingApply(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Áp mức chung sang mức riêng theo tỷ lệ (hàng loạt)</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-2">
          <Form.Label>Chi nhánh</Form.Label>
          <Form.Select value={branchId} onChange={(e) => invalidateOnChange(setBranchId)(e.target.value)}>
            <option value="">-- Chọn chi nhánh --</option>
            {(branches || []).map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>Áp cho</Form.Label>
          <Form.Select value={target} onChange={(e) => invalidateOnChange(setTarget)(e.target.value)}>
            {TARGET_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>Tỷ lệ (%)</Form.Label>
          <Form.Control
            type="number"
            min="0.01"
            step="0.01"
            value={ratio}
            onChange={(e) => invalidateOnChange(setRatio)(e.target.value)}
            placeholder="Ví dụ: 80 (giảm còn 80%) hoặc 120 (tăng lên 120%)"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="apply-ratio-overwrite"
            label="Ghi đè cả những mức riêng đã đặt tay"
            checked={overwriteExisting}
            onChange={(e) => invalidateOnChange(setOverwriteExisting)(e.target.checked)}
          />
        </Form.Group>

        <div className="d-flex mb-3">
          <Button variant="outline-primary" onClick={handlePreview} disabled={loadingPreview}>
            {loadingPreview ? <Spinner size="sm" /> : 'Xem trước'}
          </Button>
        </div>

        {preview && (
          <>
            <Alert variant="info" className="py-2 small">
              Sẽ đặt <strong>{preview.summary.applied}</strong> NVL
              {preview.summary.overwritten > 0 && <> (đè <strong>{preview.summary.overwritten}</strong>)</>}
              . Bỏ qua <strong>{preview.summary.skipped_no_base}</strong> NVL chưa có mức chung
              {preview.summary.skipped_zero > 0 && <>, <strong>{preview.summary.skipped_zero}</strong> NVL ra 0 sau làm tròn</>}
              {preview.summary.skipped_existing_no_overwrite > 0 && (
                <>, <strong>{preview.summary.skipped_existing_no_overwrite}</strong> NVL đã có mức riêng (bật "Ghi đè" ở trên để đổi)</>
              )}
              .
            </Alert>
            {overwriteExisting && preview.summary.overwritten > 0 && (
              <Alert variant="warning" className="py-2 small">
                Cảnh báo: thao tác này sẽ ghi đè {preview.summary.overwritten} mức riêng do người dùng tự đặt tay trước đó.
              </Alert>
            )}
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              <Table bordered hover responsive size="sm">
                <thead className="table-light">
                  <tr>
                    <th>NVL</th>
                    {targetKeys.map((k) => <th key={k} className="text-end">{TARGET_LABEL[k]}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((item) => {
                    const mat = materialById.get(item.material_id);
                    return (
                      <tr key={item.material_id}>
                        <td className="fw-medium">{item.material_name}</td>
                        {targetKeys.map((k) => (
                          <TargetCell key={k} cell={item[k]} material={mat} />
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loadingApply}>Hủy</Button>
        <Button
          variant="primary"
          onClick={handleApply}
          disabled={!preview || loadingApply || preview.summary.applied === 0}
        >
          {loadingApply ? 'Đang áp dụng...' : 'Áp dụng'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
